import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Button,
} from '@mui/material';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const DEFAULT_CENTER = [46.5, 2.5];

/* ------------------------------------------------------------------
   Helpers
-------------------------------------------------------------------*/
function useColorScale(facilities) {
  const [min, max] = useMemo(() => {
    if (!facilities.length) return [0, 5];
    const notes = facilities.map((f) => Number(f.note) || 0);
    return [Math.min(...notes), Math.max(...notes) || 5];
  }, [facilities]);

  return (note) => {
    const n = Number(note) || 0;
    const ratio = (n - min) / (max - min || 1);
    const r = Math.round(255 * (1 - ratio));
    const g = Math.round(180 * ratio + 60);
    return `rgb(${r},${g},80)`;
  };
}

function FitMapToBounds({ positions, disabled }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length || disabled) return;
    map.fitBounds(positions, { padding: [60, 60], maxZoom: 6 });
  }, [positions, map, disabled]);
  return null;
}

function starIcon(note) {
  const size = 16 + (note ?? 0) * 5;
  const color = note >= 4 ? '#FFD700' : note >= 2 ? '#C0C0C0' : '#cd7f32';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}"><path d="M12 .587l3.668 7.431 8.2 1.151-5.934 5.574 1.439 8.084L12 18.896l-7.373 3.931 1.439-8.084L.132 9.169l8.2-1.151z"/></svg>`;
  return L.divIcon({ className: '', html: svg, iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2] });
}

function AddressSearch({ onSearch, isMapReady }) {
  const [address, setAddress] = useState('');

  const handleSearch = async () => {
    if (!address.trim()) return;
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`;
    console.log('🔎 Searching:', url);
    const res = await fetch(url);
    const data = await res.json();
    if (data.length) {
      const { lat, lon } = data[0];
      const coords = [parseFloat(lat), parseFloat(lon)];
      console.log('📍 Found coords:', coords);
      onSearch(coords);
    } else console.warn('Aucun résultat.');
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <TextField label="Adresse" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
      <Button variant="contained" onClick={handleSearch} disabled={!isMapReady || !address.trim()}>Chercher</Button>
    </Box>
  );
}

/* ------------------------------------------------------------------
   Component
-------------------------------------------------------------------*/
export default function MainMap() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);   // ✅ new state
  const [hasSearched, setHasSearched] = useState(false);

  /* Fetch facilities */
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/GetFacilityWithNote`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        setFacilities(json.filter((f) => f.lat && f.lon));
      } catch (err) {
        console.error(err);
        setError('Impossible de charger les facilities.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const colorOf = useColorScale(facilities);
  const positions = facilities.map((f) => [f.lat, f.lon]);

  /* Search handler */
  const handleSearchLocation = (coords) => {
    if (mapRef.current) {
      console.log('🗺️ flyTo', coords);
      mapRef.current.flyTo(coords, 16, { duration: 1.3 });
      setHasSearched(true);
    } else console.warn('Map not ready');
  };

  /* Render */
  if (loading) return (<Box mt={6} textAlign="center"><CircularProgress /><Typography mt={2}>Chargement…</Typography></Box>);
  if (error) return (<Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Facilities – Carte des notes</Typography>

      <AddressSearch onSearch={handleSearchLocation} isMapReady={mapReady} />

      <Paper elevation={3} sx={{ p: 0 }}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={5}
          style={{ width: '100%', height: 600 }}
          scrollWheelZoom={false}
           whenReady={(e) => {
            mapRef.current = e.target;  // Leaflet map instance
            console.log('✅ Map ready');
            setMapReady(true);
          }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitMapToBounds positions={positions} disabled={hasSearched} />

          {facilities.map((f) => (
            <Marker key={f.id} position={[f.lat, f.lon]} icon={starIcon(f.note)} eventHandlers={{ click: () => setSelected(f) }}>
              {selected?.id === f.id && (
                <Popup onClose={() => setSelected(null)}>
                  <strong>{f.name}</strong><br />
                  Note : {Number(f.note).toFixed(2)}
                </Popup>
              )}
            </Marker>
          ))}
        </MapContainer>
      </Paper>
    </Container>
  );
}
